import { HAWK_HOVER } from '../../models/learned.ts';
import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inOut, inQuad, keys, lerp, outBack, outQuad, phase, smooth, wave, type Ease } from '../curves.ts';
import { timeline, type Ch, type KeyPose } from '../pose.ts';

/*
 * The ranger's hawk (models/learned.ts): a goshawk that hunts for the ranger. It circles at its hover height on
 * broad, slotted wings, rows on with deep beats when it chases, and stoops on its prey: wings thrown up, folded
 * into a dive, flared at the last moment with the talons thrown forward, then it beats back up to the hover.
 *
 * Wings as the soot crow's (anim/creatures/crow.ts): on the right one (+X) +Z on `arm` lifts it, +Y sweeps it
 * back, +X turns the leading edge down; on `hand` +Y folds the primaries back. The left mirrors. +X pitches the
 * body and head nose down, lifts the tail and swings the talons back; +Z on the root banks it, left wing down.
 *
 * The stoop is timed to the sim: the world starts `attack` `HAWK_STRIKE` seconds before the blow lands (on the
 * pulse's countdown), so the talons close on the prey on the tick the hit does. It starts and ends at the hover.
 */

type B = 'root' | 'body' | 'head' | 'armL' | 'handL' | 'armR' | 'handR' | 'tail' | 'legs';
type P = KeyPose<B>;

const sin = Math.sin;
/** Seconds into `attack` when the talons strike (five ticks of the countdown before the blow). */
export const HAWK_STRIKE = 5 / 12;
/** How far the stoop takes it: down to a nightmare's chest, and on toward it. */
const LOW = -(HAWK_HOVER - 0.62);
const REACH = 0.42;
/** The fold into the dive: it accelerates all the way down. */
const drop: Ease = (k) => k ** 1.7;

const mirror = (c: Ch): Ch => [c[0], -c[1], -c[2], -(c[3] ?? 0), c[4] ?? 0, c[5] ?? 0, c[6] ?? 1];
/** Both wings from the right one's arm and hand. */
const wings = (arm: Ch, hand: Ch = [0, 0, 0]): P => ({ armR: arm, handR: hand, armL: mirror(arm), handL: mirror(hand) });

/** A wing's pose for the right side: arm and hand rotations. */
interface W { ax: number; ay: number; az: number; hx: number; hy: number; hz: number }

/**
 * One wingbeat at `p` (0 = top of the stroke; loops at 1): a hawk's, deep and rowing. The downstroke drives
 * down and a little forward with the hand spread; on the upstroke the wrist leads, the hand half folded back.
 */
const beatAt = (p: number): W => ({
  az: keys(p, [[0, 0.85], [0.45, -0.6, inOut], [1, 0.85, inOut]]),
  ay: keys(p, [[0, 0.08], [0.25, -0.14], [0.47, 0.08], [0.72, 0.36], [1, 0.08]]),
  ax: keys(p, [[0, 0], [0.22, 0.14], [0.47, 0.04], [0.72, -0.18], [1, 0]]),
  hz: keys(p, [[0, 0.2], [0.25, 0.25], [0.5, -0.32, outQuad], [0.75, -0.42], [0.93, 0.08], [1, 0.2]]),
  hy: keys(p, [[0, 0], [0.42, 0.02], [0.6, 0.45], [0.78, 0.75], [0.95, 0.05], [1, 0]]),
  hx: keys(p, [[0, 0], [0.25, 0.08], [0.72, -0.12], [1, 0]]),
});
/** Soaring: a flat, slight dihedral, the hand a touch swept and the fingers curling up. */
const SOAR: W = { az: 0.1, ay: 0.04, ax: 0.03, hz: 0.12, hy: 0.08, hx: 0 };
const mix = (a: W, b: W, k: number): W => ({
  ax: lerp(a.ax, b.ax, k), ay: lerp(a.ay, b.ay, k), az: lerp(a.az, b.az, k), hx: lerp(a.hx, b.hx, k), hy: lerp(a.hy, b.hy, k), hz: lerp(a.hz, b.hz, k),
});
const both = (b: Bones<B>, w: W): void => {
  b.armR.rot(w.ax, w.ay, w.az);
  b.handR.rot(w.hx, w.hy, w.hz);
  b.armL.rot(w.ax, -w.ay, -w.az);
  b.handL.rot(w.hx, -w.hy, -w.hz);
};
/** What a beat does to the body: lifted on the downstroke, the head held still in space, the tail pumping. */
const lift = (b: Bones<B>, p: number, amp = 1): void => {
  b.root.pos(0, 0.05 * amp * sin(TAU * (p - 0.2)), 0);
  b.body.rot(-0.04 * amp * sin(TAU * (p - 0.05)));
  b.head.rot(0.04 * amp * sin(TAU * (p - 0.05)));
  b.tail.rot(0.1 * amp * sin(TAU * (p - 0.15)));
};

/**
 * Circling over the ranger (the world carries it round): banked into the turn on still wings, two beats once a
 * loop, the tail twisting to steer and the head turned down and in, watching the ground.
 */
const hawkIdle = (): Clip<B> =>
  clip<B>('idle', 2.4, true, (b, t) => {
    const P = phase(t, 2.4);
    const beats = 0.36;
    if (t < beats * 2) {
      // From the soar up into two beats and back down: eased in and out so the loop has no seam.
      const p = phase(t, beats);
      const k = envelope(t, 0, 0.12, beats * 2 - 0.12, beats * 2, smooth);
      both(b, mix(SOAR, beatAt(p), k));
      lift(b, p, k);
    } else {
      const u = (t - beats * 2) / (2.4 - beats * 2);
      both(b, SOAR);
      // The fingers flex in the air of the glide.
      b.handR.rot(0, 0, 0.06 * sin(TAU * u * 2));
      b.handL.rot(0, 0, -0.06 * sin(TAU * u * 2 + 0.7));
    }
    b.root.pos(0, 0.04 * wave(P, 0.3), 0).rot(0.04 * wave(P, 0.1), 0, 0.3 + 0.06 * wave(P, 0.6));
    b.body.rot(0.06);
    b.head.rot(0.35 + 0.05 * wave(P, 0.2), 0.3 + 0.12 * wave(P, 0.45), 0.18);
    b.tail.rot(-0.05 + 0.04 * wave(P, 0.4), 0.06 * wave(P, 0.15), 0.18 + 0.08 * wave(P, 0.7));
    b.legs.rot(0.5);
  });

/** Chasing its prey: four hard, fast beats and a short glide, flat out, head forward, talons tucked. */
const hawkWalk = (): Clip<B> =>
  clip<B>('walk', 1.2, true, (b, t) => {
    const P = phase(t, 1.2);
    const beat = 0.24;
    if (t < beat * 4) {
      const p = phase(t, beat);
      both(b, beatAt(p));
      lift(b, p, 1.2);
    } else {
      // From the top of the last upstroke into the glide and up again for the next beat.
      const u = (t - beat * 4) / (1.2 - beat * 4);
      both(b, mix(beatAt(0), SOAR, envelope(u, 0, 0.35, 0.65, 1, inOut)));
    }
    b.root.pos(0, 0.04 * wave(P, 0.1), 0).rot(0, 0, 0.06 * wave(P, 0.55));
    b.body.rot(0.12);
    b.head.rot(-0.08, 0.1 * wave(P, 0.7), 0);
    b.tail.rot(0.04, 0.06 * wave(P, 0.45), 0);
    b.legs.rot(0.6);
  });

/**
 * The stoop. Over the countdown it throws the wings up (0.08 s), folds them along its back and drops nose first
 * onto its prey; at the last instant it flares, the wings spread forward to brake and the talons thrown out
 * (`HAWK_STRIKE`), rakes, mantles over the prey and beats back up to the hover by 1 s.
 */
const hawkAttack = (): Clip<B> =>
  clip<B>('attack', 1.0, false, (b, t) => {
    const S = HAWK_STRIKE;
    timeline(b, t, [
      [0, {}],
      [0.1, { root: [-0.25, 0, 0, 0, 0.14, -0.06], body: [-0.3, 0, 0], head: [0.25, 0, 0], tail: [-0.2, 0, 0], legs: [0.3, 0, 0], ...wings([0, 0.25, 1.0], [0, 0.2, 0.3]) }, outQuad],
      [S - 0.1, { root: [0.7, 0, 0, 0, LOW * 0.7, REACH * 0.7], body: [0.25, 0, 0], head: [-0.4, 0, 0], tail: [0.1, 0, 0], legs: [0.5, 0, 0], ...wings([0.1, 1.3, 0.2], [0, 1.1, -0.15]) }, drop],
      [S, { root: [-0.2, 0, 0, 0, LOW, REACH], body: [-0.55, 0, 0], head: [0.55, 0, 0], tail: [-0.45, 0, 0], legs: [-1.25, 0, 0], ...wings([-0.35, -0.25, 0.55], [0, -0.1, 0.25]) }, outQuad],
      [S + 0.14, { root: [-0.05, 0, 0, 0, LOW + 0.04, REACH], body: [-0.25, 0, 0.05], head: [0.65, 0.2, 0], tail: [-0.3, 0, 0], legs: [-0.7, 0, 0], ...wings([-0.15, -0.05, -0.35], [0, 0, -0.3]) }, outBack],
      [S + 0.28, { root: [-0.15, 0, 0, 0, LOW * 0.45, REACH * 0.55], body: [-0.2, 0, 0], head: [0.2, 0, 0], tail: [-0.15, 0, 0], legs: [0.1, 0, 0], ...wings([0.1, -0.12, -0.7], [0, 0, -0.4]) }, inOut],
      [S + 0.42, { root: [-0.05, 0, 0, 0, LOW * 0.12, REACH * 0.15], body: [-0.05, 0, 0], legs: [0.35, 0, 0], ...wings([0, 0.3, 0.85], [0, 0.5, 0.2]) }, inOut],
      [1.0, {}, inOut],
    ], { head: 0.015, tail: 0.03, legs: 0.02, handL: 0.02, handR: 0.02 });
  }, (t) => {
    // Stretched long in the dive, and a beat of squash as it brakes.
    const k = keys(t, [[0, 0], [HAWK_STRIKE - 0.06, 1, inQuad], [HAWK_STRIKE + 0.06, -0.6], [HAWK_STRIKE + 0.25, 0]]);
    return k >= 0 ? [1 - 0.08 * k, 1 - 0.06 * k, 1 + 0.14 * k] : [1 - 0.06 * k, 1 + 0.04 * k, 1 + 0.05 * k];
  });

export const hawkClips = (): Clip[] => [hawkIdle(), hawkWalk(), hawkAttack()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = { 'bot:hawk': hawkClips };
